// „Wie lange war der eingeloggt?" — Alex' Anforderung am Login-Fehler, wörtlich
// und bewusst eng: keine Auswertung, keine Zeitreihe, kein Dashboard. Eine Zahl.
//
// Quelle: Login, GET /api/token-stats/agent/{name} (arkserver, Commit 4ff89fb).
// Geliefert werden ROHE Werte (`age_min` als Zahl) — die Formulierung entsteht
// hier, damit sie zur Oberfläche passt und mitlaufen kann.

export interface TokenStats {
  bot?: string;
  /** Absolute Zeitstempel (epoch, Sekunden). Der Grund, warum die Anzeige
   *  mitlaufen KANN: aus ihnen lässt sich jederzeit neu rechnen, während
   *  `age_min`/`expires_in_min` nur die Wahrheit des Abrufmoments sind. */
  set_at?: number;
  expires_at?: number;
  /** Alter des aktuellen Tokens in Minuten. Der einzige Wert, den Alex verlangt hat. */
  age_min?: number;
  expires_in_min?: number;
  verdict?: string;
  fingerprint?: string;
  node?: string;
  /** Ein von Hand gesetzter Token hat keine Herkunft — false ist NORMALFALL,
   *  kein Fehler (Login, 2026-08-07). `age_min` stimmt trotzdem. */
  history_available?: boolean;
}

/** „4 h 27 min" / „26 min" / „2 d 3 h". Bewusst ohne „vor" — die Zahl beantwortet
 *  „wie lange war er eingeloggt", nicht „wann war das". */
export function formatLoginAge(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '';
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/** Urteil in Alltagssprache. Die Enum-Werte sind Logins Vokabular, nicht das
 *  des Betrachters — und der Unterschied zwischen „abgelaufen" und „nach
 *  Rotation veraltet" ist genau der, den Alex sehen will: einmal ist die Zeit
 *  um, einmal wurde der Token ersetzt und dieser hier blieb zurück. */
const VERDICT_LABEL: Record<string, string> = {
  ok: 'gültig',
  expired: 'abgelaufen',
  stale_after_rotation: 'nach Rotation veraltet',
  revoked: 'widerrufen',
  missing: 'kein Token hinterlegt',
};

export function formatVerdict(verdict: string | null | undefined): string {
  if (!verdict) return '';
  // Unbekannte Urteile werden DURCHGEREICHT statt verschluckt: ein neuer
  // serverseitiger Zustand soll sichtbar sein, auch wenn ich ihn noch nicht
  // übersetzt habe.
  return VERDICT_LABEL[verdict] || verdict;
}

/** „seit 11 h abgelaufen" aus `expires_in_min`. Negativ heißt: schon vorbei.
 *  Ist der Wert positiv, läuft der Token noch — dann sagt die Zeile nichts,
 *  denn dann ist die Ablaufzeit nicht die Ursache des Problems. */
export function formatExpiredSince(expiresInMin: number | null | undefined): string {
  if (expiresInMin == null || !Number.isFinite(expiresInMin) || expiresInMin >= 0) return '';
  const d = formatLoginAge(Math.abs(expiresInMin));
  return d ? `seit ${d} abgelaufen` : '';
}

/**
 * Rechnet Alter und Restlaufzeit aus den ABSOLUTEN Zeitstempeln gegen die
 * aktuelle Uhr — statt die Momentaufnahme `age_min`/`expires_in_min` zu
 * konservieren.
 *
 * Grund (und ich habe genau das Login gegenüber selbst eingefordert): Eine
 * Dauer, die beim Abruf berechnet und dann eingefroren wird, ist mit jeder
 * Minute falscher. Bleibt ein Agent eine Stunde blockiert, stand dort bisher
 * unverändert „seit 10 h abgelaufen", obwohl es längst elf sind.
 */
export function liveAges(t: TokenStats, nowMs: number): { ageMin?: number; expiresInMin?: number } {
  const now = nowMs / 1000;
  return {
    ageMin: t.set_at != null ? (now - t.set_at) / 60 : t.age_min,
    expiresInMin: t.expires_at != null ? (t.expires_at - now) / 60 : t.expires_in_min,
  };
}
